// File: src/components/ModalComponent.vue
<template>
    <Teleport to="body">
        <transition name="fade" mode="out-in">
            <div v-if="internalVisible" class="modal-backdrop-wrapper">
                <!-- Backdrop -->
                <div class="modal-backdrop"></div>

                <!-- Accessible native modal -->
                <dialog
                    ref="dialogRef"
                    open
                    :style="{ width }"
                    class="modal-content-dialog"
                    @cancel.prevent="handleClose"
                    @keydown.esc.prevent.stop="handleClose"
                    v-bind="$attrs"
                    :role="props.dialogRole"
                    aria-modal="true"
                    :aria-labelledby="ariaLabelledby"
                    :aria-describedby="ariaDescribedby"
                    tabindex="-1"
                >
                    <!-- Header -->
                    <header class="modal-header">
                        <div :id="modalTitleId" tabindex="-1">
                            <slot name="header"></slot>
                        </div>
                        <button
                            v-if="showHelp"
                            type="button"
                            class="btn btn-sm btn-outline-info learn-more-btn"
                            @click="isHelpOpen = true"
                            :title="$t ? $t('common.learnMore') : 'Learn More'"
                            :aria-label="$t ? $t('common.learnMore') : 'Learn More'"
                        >
                            {{ $t ? $t('common.learnMore') : 'Learn More' }}
                        </button>
                    </header>

                    <!-- Body -->
                    <section :id="modalDescId" class="body">
                        <slot name="body"></slot>
                    </section>

                    <!-- Footer -->
                    <footer>
                        <slot name="footer"></slot>
                    </footer>

                    <!-- Help Modal -->
                    <HelpModal
                        v-if="showHelp"
                        :isVisible="isHelpOpen"
                        :title="helpTitle"
                        :content="helpContent"
                        @close="isHelpOpen = false"
                    />
                </dialog>
            </div>
        </transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, useSlots, type VNodeChild } from 'vue'
import HelpModal from '@/components/modals/HelpModal.vue'
import { useFocusTrap } from '@/composables/useFocusTrap'

defineSlots<{
    header?: () => VNodeChild
    body?: () => VNodeChild
    footer?: () => VNodeChild
}>()

defineOptions({ inheritAttrs: false }) // only pass attrs to <dialog>

// Module-scope counter: unique across all instances
let modalInstanceCounter = 0

// Per-instance unique suffix (deterministic; not crypto)
const uid = `${Date.now()}-${++modalInstanceCounter}`
const modalTitleId = `modal-title-${uid}`
const modalDescId = `modal-desc-${uid}`

/**
 * Props interface for ModalComponent
 */
interface Props {
    /** Controls modal visibility (v-model) */
    isVisible: boolean
    /** Width of the modal (CSS value) */
    width?: string
    /** Show help button and modal */
    showHelp?: boolean
    /** Title for help modal */
    helpTitle?: string
    /** Content for help modal */
    helpContent?: string
    /**
     * ARIA role for the modal container.
     * Use 'alertdialog' for destructive confirmations.
     */
    dialogRole?: 'dialog' | 'alertdialog'
    /**
     * ARIA labelledby reference (ID of title element).
     * If not provided, falls back to internal modalTitleId.
     */
    ariaLabelledby?: string
    /**
     * ARIA describedby reference (ID of description element).
     * If not provided, falls back to internal modalDescId.
     */
    ariaDescribedby?: string
    /**
     * CSS selector or element to focus on open.
     * Defaults to the modal title for WCAG compliance.
     * Set to null to focus the dialog container itself.
     */
    initialFocus?: string | HTMLElement | null
}

const props = withDefaults(defineProps<Props>(), {
    width: '60%',
    showHelp: false,
    helpTitle: 'Help',
    helpContent: '',
    dialogRole: 'dialog'
    // initialFocus defaults to undefined, will use computed modalTitleId selector
})

// keep legacy 'close' for callers still listening to it
const emit = defineEmits<{
    /** Emitted when modal visibility changes */
    'update:isVisible': [value: boolean]
    /** Legacy close event for backward compatibility */
    close: []
}>()

const dialogRef = ref<HTMLDialogElement | null>(null)
const isHelpOpen = ref<boolean>(false)
const slots = useSlots()

// Computed ARIA attributes: use explicit props or fallback to internal IDs
const ariaLabelledby = computed(
    () => props.ariaLabelledby ?? (slots.header ? modalTitleId : undefined)
)
const ariaDescribedby = computed(
    () => props.ariaDescribedby ?? (slots.body ? modalDescId : undefined)
)

// Computed initialFocus selector (reactive to prop changes)
const resolvedInitialFocus = computed(() => props.initialFocus ?? `#${modalTitleId}`)

// Computed v-model proxy for proper two-way visibility sync
const internalVisible = computed<boolean>({
    get: () => props.isVisible,
    set: (val) => {
        emit('update:isVisible', val)
        // Only emit 'close' when transitioning from visible to hidden (backward compat)
        if (val === false) emit('close')
    }
})

// Focus trap for accessibility
const { activate: activateFocusTrap, deactivate: deactivateFocusTrap } = useFocusTrap(dialogRef, {
    lockScroll: true
})

/** Close handler for cancel events and explicit close actions */
const handleClose = (): void => {
    internalVisible.value = false
}

/**
 * Focus and trap setup when the modal becomes visible.
 * Extracted to avoid duplication between watch and onMounted.
 */
const handleOpen = async (): Promise<void> => {
    await nextTick()

    // Ensure dialog container is focusable as fallback
    if (dialogRef.value && dialogRef.value.tabIndex < 0) {
        dialogRef.value.setAttribute('tabindex', '-1')
    }

    // Activate trap first, then focus target (safer order)
    activateFocusTrap()
    await nextTick()

    // Focus initial element (guarded to dialog subtree)
    const focusTarget = resolvedInitialFocus.value
    if (typeof focusTarget === 'string') {
        // Query within dialog only to avoid collisions
        const element = dialogRef.value?.querySelector(focusTarget) as HTMLElement | null
        element?.focus()
    } else if (focusTarget instanceof HTMLElement) {
        focusTarget.focus()
    } else if (dialogRef.value) {
        // Fallback to dialog container
        dialogRef.value.focus()
    }
}

/** Activate/deactivate focus trap when visibility changes */
watch(internalVisible, async (isVisible) => {
    if (isVisible) await handleOpen()
    else deactivateFocusTrap()
})

// Handle components that mount already visible (v-if render pattern)
onMounted(async () => {
    if (internalVisible.value) await handleOpen()
})
</script>

<style scoped lang="scss">
/* Backdrop wrapper for fullscreen dim effect */
.modal-backdrop-wrapper {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    justify-content: center;
    align-items: center; // centers the dialog vertically
}

/* Darkened screen backdrop */
.modal-backdrop {
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(1px);
    z-index: 0;
}

/* Actual modal content using <dialog> */
dialog.modal-content-dialog {
    border: none;
    border-radius: 12px;
    padding: 0;
    margin: auto;
    max-width: 92vw;
    width: 60%;
    background-color: var(--bs-body-bg);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    z-index: 1;
}

/* Modal Header */
.modal-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 20px;
    background-color: var(--modal-header);
    border-bottom: 1px solid var(--border-color);
    border-radius: 8px 8px 0 0;

    h1,
    h2 {
        margin: 0;
        font-weight: bold;
    }

    h1 {
        font-size: 1.5em;
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
        color: var(--body-color-secondary);
        margin-bottom: 5px;
    }

    h2 {
        font-size: 1em;
        color: var(--modal-sub-heading);
    }
}

/* Modal Body */
section.body {
    position: relative;
    width: 90%;
    max-width: 100%;
    padding: 20px 20px 50px;
    margin: auto;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;

    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #c1c1c1;
        border-radius: 4px;

        &:hover {
            background: #a8a8a8;
        }
    }

    /* Firefox scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: #c1c1c1 #f1f1f1;
}

/* Modal Footer */
footer {
    display: flex;
    justify-content: center;
    gap: 20px;
    padding: 20px;
    margin: 15px 20px;
    border-top: 1px solid var(--border-color);
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
